import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const UserReportsContext = createContext();

export const useUserReports = () => {
  const context = useContext(UserReportsContext);
  if (!context) {
    throw new Error('useUserReports must be used within a UserReportsProvider');
  }
  return context;
};

export const UserReportsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getReportsBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/user-reports/api/v1/user-reports`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/user-reports/api/v1/user-reports`;
    } else {
      throw new Error('Invalid login type for user reports access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access user reports');
    }
  }, [loginType]);

  // Create a new report (Usually done by users, but available for testing/admin creation)
  const createReport = useCallback(async (reportData) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getReportsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData)
      });
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getReportsBaseUrl]);

  // Get reports with pagination and filtering
  const getReports = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.resolved !== undefined) queryParams.append('resolved', options.resolved);
      if (options.profileId) queryParams.append('profile_id', options.profileId);

      const baseUrl = getReportsBaseUrl();
      const url = `${baseUrl}/`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch reports';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getReportsBaseUrl]);

  // Get a specific report by ID
  const getReportById = useCallback(async (reportId) => {
    validateAccess();
    
    if (!reportId) {
      throw new Error('Report ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getReportsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(reportId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get report details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getReportsBaseUrl]);

  // Resolve a report with a verdict
  const resolveReport = useCallback(async (reportId, verdict) => {
    validateAccess();

    if (!reportId) throw new Error('Report ID is required');
    if (!verdict) throw new Error('Verdict is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getReportsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${encodeURIComponent(reportId)}/resolve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verdict })
      });
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to resolve report';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getReportsBaseUrl]);

  // Convenience: Get pending reports (unresolved)
  const getPendingReports = useCallback(async (options = {}) => {
    validateAccess();
    return await getReports({ ...options, resolved: 'false' });
  }, [getReports, validateAccess]);

  // Convenience: Get resolved reports
  const getResolvedReports = useCallback(async (options = {}) => {
    validateAccess();
    return await getReports({ ...options, resolved: 'true' });
  }, [getReports, validateAccess]);

  // Convenience: Get reports for a specific business profile
  const getReportsByProfile = useCallback(async (profileId, options = {}) => {
    validateAccess();
    if (!profileId) throw new Error('Profile ID is required');
    return await getReports({ ...options, profileId });
  }, [getReports, validateAccess]);

  // Export reports data (Prepare for CSV/Excel)
  const exportReportsData = useCallback(async (filters = {}) => {
    validateAccess();

    // Fetch large batch for export
    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0
    };

    const result = await getReports(exportOptions);

    const exportData = result.reports.map(report => ({
      'Report ID': report.report_id,
      'Date': report.report_date,
      'Time': report.report_time,
      'Profile ID': report.profile_id,
      'User ID': report.user_id,
      'Reason': report.report_reason,
      'Status': report.report_resolved ? 'Resolved' : 'Pending',
      'Verdict': report.report_verdict || 'N/A',
      'Resolved At': report.report_resolved_timestamp || 'N/A'
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      filename: `user_reports_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getReports, validateAccess]);

  // Health check for the service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      // Health check is at the service root /health, distinct from API root
      // Based on main.go, it maps to /admin/user-reports/health or /master/user-reports/health
      const serviceRoot = loginType === 'master' 
        ? `${API_BASE_URL}/master/user-reports`
        : `${API_BASE_URL}/admin/user-reports`;
        
      const response = await makeAuthenticatedRequest(`${serviceRoot}/health`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Format report data for UI display
  const formatReportData = useCallback((report) => {
    const getStatusColor = () => {
      if (!report.report_resolved) return 'orange'; // Pending
      if (report.report_verdict === 'banned') return 'red';
      if (report.report_verdict === 'dismissed') return 'gray';
      return 'green'; // Resolved/Other
    };

    const getVerdictDisplay = () => {
      if (!report.report_resolved) return 'Pending Review';
      // Capitalize first letter of verdict
      const v = report.report_verdict || 'Resolved';
      return v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ');
    };

    return {
      ...report,
      statusColor: getStatusColor(),
      statusText: report.report_resolved ? 'Resolved' : 'Pending',
      verdictDisplay: getVerdictDisplay(),
      fullDateTime: `${report.report_date} ${report.report_time}`,
      isBanned: report.report_verdict === 'banned',
      isDismissed: report.report_verdict === 'dismissed',
    };
  }, []);

  // Debugging helper
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/user-reports/api/v1/user-reports`
        : `${API_BASE_URL}/admin/user-reports/api/v1/user-reports`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    createReport,
    getReports,
    getReportById,
    resolveReport,
    
    // Convenience Filters
    getPendingReports,
    getResolvedReports,
    getReportsByProfile,
    
    // Utilities
    exportReportsData,
    checkHealth,
    formatReportData,
    getEndpointInfo,
    clearError,
    
    // Computed Accessors
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasReportsAccess: loginType === 'admin' || loginType === 'master',
  };

  return (
    <UserReportsContext.Provider value={value}>
      {children}
    </UserReportsContext.Provider>
  );
};